import { IconInfoCircle } from '@tabler/icons-react';
import { Alert } from '@mantine/core';
import {
  Controls,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useKeyPress,
  useNodesState,
} from '@xyflow/react';
import { useCallback, useEffect, useRef, useState } from 'react';
// you also need to adjust the style import
import '@xyflow/react/dist/style.css';
import { v4 as uuidv4 } from 'uuid';
import { isUnconfiguredConditionNode } from '../../helpers/flowNodes';
import AbSplitNode from './AbSplitNode';
import ConditionalNode from './ConditionalNode';
import DefaultEdge from './DefaultEdge';
import DefaultNode from './DefaultNode';
import FlowSidebar from './FlowSidebar';
import StartNode from './StartNode';

const edgeTypes = {
  default: DefaultEdge,
};

const nodeTypes = {
  default: DefaultNode,
  conditional: ConditionalNode,
  abSplit: AbSplitNode,
  start: StartNode,
};

const WHATSAPP_PLACEHOLDER_RE = /\{\{\s*(var\d+|var_h\d+|\d+)\s*\}\}/;

const Flow = ({
  setIsGoogleNode,
  setHasUnconfiguredConditionNode,
  reactFlowInstance,
  setReactFlowInstance,
  availableCustomFields,
  eventTags,
  eventActions,
  emails,
  campaigns,
  viberMessages,
  textMessages,
  pushNotifications,
  facebookAds,
  nodes: passedNodes,
  edges: passedEdges,
  flowDataRef,
  readOnly,
  pages,
  subscriptions,
  segments,
  whatsappMessages,
  surveys,
  remainingDailyQuotaWhatsappMessages,
  googleAds,
  webhooks,
  campaignMode,
}) => {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    passedNodes.map(({ width, height, measured, ...node }) => node)
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    passedEdges.map((e) => {
      return {
        ...e,
        invertSelection: () => toggleInvertSelection(e.id),
      };
    })
  );
  const [selection, setSelection] = useState({ nodes: [], edges: [] });
  const deletePressed = useKeyPress('Delete');

  useEffect(() => {
    const googleAdNodeExists = nodes.some((n) => n.data?.custom?.key === 'sendGoogleAd');
    setIsGoogleNode(googleAdNodeExists);
  }, [nodes, setIsGoogleNode]);

  useEffect(() => {
    setHasUnconfiguredConditionNode(nodes.some(isUnconfiguredConditionNode));
  }, [nodes, setHasUnconfiguredConditionNode]);

  useEffect(() => {
    if (flowDataRef) {
      flowDataRef.current = { nodes, edges };
    }
  }, [nodes, edges, flowDataRef]);

  const updateItem = (flowItem) => {
    reactFlowInstance.setNodes((nodes) => {
      return [...nodes.filter((n) => n.id !== flowItem.id), { ...flowItem }];
    });
  };

  const onDelete = useCallback(() => {
    if (!selection || readOnly) {
      return;
    }
    setNodes((nodes) => nodes.filter((n) => n.id === 'start' || !selection.nodes.includes(n.id)));
    setEdges((edges) =>
      edges
        .filter((n) => !selection.edges.includes(n.id))
        .filter((e) => {
          const nonStartNodes = selection.nodes.filter((n) => n !== 'start');
          return !nonStartNodes.includes(e.source) && !nonStartNodes.includes(e.target);
        })
    );
  }, [selection, readOnly, setNodes, setEdges]);

  useEffect(() => {
    if (deletePressed) {
      onDelete();
    }
  }, [deletePressed, onDelete]);

  const removeEdge = useCallback(
    (id) => {
      if (readOnly) {
        return;
      }
      setEdges((edges) => edges.filter((e) => e.id !== id));
    },
    [setEdges, readOnly]
  );

  const toggleInvertSelection = useCallback(
    (id) => {
      setEdges((edges) => {
        const edge = edges.find((e) => e.id === id);
        const edgesToToggle = edges.filter((e) => e.source === edge.source);
        return [
          ...edges.filter((e) => !edgesToToggle.map((ee) => ee.id).includes(e.id)),
          ...edgesToToggle.map((e) => {
            return {
              ...e,
              data: {
                ...e?.data,
                invertSelection: !e?.data?.invertSelection,
              },
            };
          }),
        ];
      });
    },
    [setEdges]
  );

  const onConnect = useCallback(
    (params) => {
      if (params.source === params.target) {
        return;
      }

      const id = uuidv4();
      const sourceNode = nodes.find((e) => e.id === params.source);
      setEdges((eds) => {
        let invertSelection = false;
        if (sourceNode?.data?.label === 'Condition') {
          invertSelection = !eds.find((e) => sourceNode.id === e.source)?.data?.invertSelection;
        }
        const newEdge = {
          ...params,
          id,
          type: 'default',
          data: {
            ...params.data,
            invertSelection,
          },
        };
        return addEdge(newEdge, eds);
      });
    },
    [nodes, setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onSelectionChange = ({ nodes, edges }) => {
    const newSelection = { nodes: nodes.map((n) => n.id), edges: edges.map((e) => e.id) };
    if (JSON.stringify(newSelection) !== JSON.stringify(selection)) {
      setSelection(newSelection);
    }
  };

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const node = JSON.parse(event.dataTransfer.getData('application/reactflow'));

      if (node.data.custom.key === 'sendGoogleAd') {
        const googleAdNodeExists = nodes.some((existingNode) => existingNode.data?.custom?.key === 'sendGoogleAd');
        if (googleAdNodeExists) {
          return;
        }
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const id = uuidv4();
      const newNode = {
        ...node,
        position,
        id,
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes, nodes]
  );

  if (
    emails === undefined ||
    pushNotifications === undefined ||
    textMessages === undefined ||
    viberMessages === undefined ||
    facebookAds === undefined ||
    campaigns === undefined ||
    eventTags === undefined ||
    eventActions === undefined ||
    availableCustomFields === undefined ||
    pages === undefined ||
    segments === undefined ||
    whatsappMessages === undefined ||
    surveys === undefined ||
    googleAds === undefined ||
    webhooks === undefined
  ) {
    return '';
  }

  const flowTypeOptions = {
    sendPushNotification: {
      id: pushNotifications.map((e) => {
        return { label: e.name, value: e.id.toString() };
      }),
    },
    sendEmail: {
      id: emails.map((e) => {
        return { label: e.name, value: e.id.toString() };
      }),
    },
    sendTextMessage: {
      id: textMessages.map((e) => {
        return { label: e.name, value: e.id.toString() };
      }),
    },
    sendViberMessage: {
      id: viberMessages.map((e) => {
        return { label: e.name, value: e.id.toString() };
      }),
    },
    sendFacebookAd: {
      id: facebookAds.map((e) => {
        return { label: e.name, value: e.id.toString() };
      }),
      lookalikeType: [
        { label: 'Reach', value: 'reach' },
        { label: 'Similarity', value: 'similarity' },
        { label: 'Custom ratio', value: 'custom_ratio' },
      ],
      lookalikeRatio: Array.from({ length: 20 }, (_, i) => ({
        label: `${(i + 1).toString()}%`,
        value: i + 1,
      })),
    },
    sendGoogleAd: {
      id: googleAds.map((e) => {
        return { label: e.name, value: e.id.toString() };
      }),
    },
    sendWhatsappMessage: {
      // Imported templates may still contain placeholder variables ({{var1}},
      // {{var_h1}}, raw {{1}}) until the user renames them to real Releva merge
      // tags via the edit page. Sending such a template would result in
      // empty parameters at Meta and a rejected send, so disable selection
      // here with a tooltip explaining what's wrong. We scan body, header,
      // btnText, AND btnUrl because Meta supports parameterized button URLs
      // and CTA text — any of these can carry a positional placeholder.
      id: whatsappMessages.map((e) => {
        const hasUnmappedVars = [e.body, e.header, e.btnText, e.btnUrl].some(
          (field) => WHATSAPP_PLACEHOLDER_RE.test(field || '')
        );
        return {
          label: e.name,
          value: e.id.toString(),
          disabled: hasUnmappedVars,
          disabledReason: hasUnmappedVars
            ? 'This template still contains unmapped placeholders ({{var1}}, {{var_h1}}, ...). Edit the template and replace them with Releva merge tags before using it in a workflow.'
            : undefined,
        };
      }),
    },
    sendWebhook: {
      id: webhooks.map((e) => {
        return { label: e.name, value: e.id.toString() };
      }),
    },
    wait: {
      waitMode: [
        { label: 'Static (fixed hours)', value: 'static' },
        ...(campaignMode === 'triggered' ? [{ label: 'Dynamic (event date)', value: 'dynamic' }] : []),
      ],
    },
  };

  return (
    <div className="dndflow" style={{ minHeight: '640px' }}>
      <ReactFlowProvider>
        <FlowSidebar
          readOnly={readOnly}
          nodes={nodes}
          selection={selection}
          campaignMode={campaignMode}
          campaigns={campaigns}
          emails={emails}
          textMessages={textMessages}
          viberMessages={viberMessages}
          webhooks={webhooks}
          pushNotifications={pushNotifications}

          flowTypeOptions={flowTypeOptions}
          updateItem={updateItem}
          availableCustomFields={availableCustomFields}
          eventTags={eventTags}
          eventActions={eventActions}
          pages={pages}
          subscriptions={subscriptions}
          segments={segments}
          whatsappMessages={whatsappMessages}
          surveys={surveys}
          remainingDailyQuotaWhatsappMessages={remainingDailyQuotaWhatsappMessages}
        />
        <div className="reactflow-wrapper" ref={reactFlowWrapper} data-tour="workFlowContent">
          {readOnly ? (
            <Alert variant="light" color="yellow" icon={<IconInfoCircle size={16} />} mb="xs">
              Modifying the steps of running workflows is not allowed. If you wish to make changes to this
              workflow, please abort it and then make a copy.
            </Alert>
          ) : null}
          <ReactFlow
            nodes={nodes}
            edges={edges.map((e) => {
              return {
                ...e,
                data: {
                  ...e.data,
                  removeEdge: () => removeEdge(e.id),
                  toggleInvertSelection: () => toggleInvertSelection(e.id),
                },
              };
            })}
            onNodesChange={readOnly ? (changes) => onNodesChange(changes.filter((c) => c.type === 'position' || c.type === 'dimensions' || c.type === 'select')) : onNodesChange}
            onEdgesChange={readOnly ? (changes) => onEdgesChange(changes.filter((c) => c.type === 'select')) : onEdgesChange}
            onConnect={readOnly ? undefined : onConnect}
            onInit={setReactFlowInstance}
            onDrop={readOnly ? undefined : onDrop}
            onDragOver={readOnly ? undefined : onDragOver}
            onSelectionChange={onSelectionChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
          >
            <Controls />
          </ReactFlow>
        </div>
      </ReactFlowProvider>
    </div>
  );
};

export default Flow;
